import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useBack } from '../hooks/useBack';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isPayLockRefusal, payAlreadyPaidMessage } from '../lib/pay-lock';
import { api } from '../api/client';
import { runTicketPdfAction, type TicketPdfAction } from '../lib/ticket-pdf';
import { disposePrintFrame } from '../lib/blob-tab';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { useSyncStore } from '../lib/offline/sync-store';
import RateSheetItemPicker, { type RateSheetItemsState } from '../components/tickets/RateSheetItemPicker';
import { applyRateSheetItem, type RateSheetItem } from '../lib/rate-sheet-items';
import { reindexAfterRemoval } from '../lib/line-item-rows';
import { RowError, rowErrorRingClass, groupRowErrors, type RowErrorReason } from '../components/ui/RowError';
import {
  validateLineItems as checkLineItems,
  bannerForRowErrors,
  splitRowReasons,
  fieldErrorClass,
  clearFieldError,
  scrollRowIntoView,
} from '../lib/line-item-validation';
import { getLineItemTypeLabel, toLineItemType } from '../constants/line-item-types';
import { carryExistingLineIds, draftIdForCreate, readPrefillDraftId } from '../lib/ai-draft-lines';
import { visibleLineItemTypeOptions } from '../lib/line-item-prefs';
import { useHiddenLineItemTypes } from '../hooks/useLineItemPrefs';
import FieldTicketDisplay from '../components/tickets/FieldTicketDisplay';
import TicketHoursReadout from '../components/tickets/TicketHoursReadout';
import EquipmentHoursRuns from '../components/tickets/EquipmentHoursRuns';
import {
  equipmentUsageError,
  seedEquipmentUsageRow,
  serializeEquipmentUsage,
  stepDays,
  type EquipmentUsageRow,
} from '../lib/equipment-usage';
import LastEditedStamp from '../components/LastEditedStamp';
import { formatTicketHours, hasContradictoryTimes, ticketTimeKey } from '../lib/ticket-hours';
import { useAuth } from '../contexts/AuthContext';
import { invalidateTickets } from '../lib/tickets-invalidation';
import DatePicker from '../components/DatePicker';
import TimePicker from '../components/TimePicker';
import ConnectionTypeSelect from '../components/ConnectionTypeSelect';
import VoiceRecorder from '../components/VoiceRecorder';
import RadioGroup from '../components/ui/RadioGroup';
import ConfirmModal from '../components/ui/ConfirmModal';
import { ticketEditReverts, ticketCanSubmit, officeTicketStatusLabel } from '../lib/ticket-status';
import { JOB_TYPE_OPTIONS, toJobType } from '../constants/job-types';
import { UNIT_OPTIONS, normalizeUnit } from '../constants/units';
import { calendarDayString, formatCalendarDate, todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useStampSignatureUrl } from '../hooks/useStampSignatureUrl';

import { blurOnWheel } from '../lib/number-input';
import { fmtCurrency, fmtQty, sumMoney } from '../lib/display-labels';
import { describeAiDraft, readRateSheetResolution, type AiDraftNotice } from '../lib/rate-sheet-resolution';
import { assignTechSlot, crewId, crewLabel, findCrewById } from '../lib/crew-slot';
import {
  buildDiscountLine, discountFieldsFor, editDiscountLine, isDiscountLine, lineForWire,
  readDiscountFields, stampDiscountBase, withDiscountEstimates, withoutDiscountEstimates,
  type DiscountEdit, type DiscountFields, type DiscountSpec,
} from '../lib/ticket-discount';
import { AddDiscountButton, DiscountLineRow } from '../components/tickets/DiscountControls';
import { clearTechSlots, prefillTechSlots } from '../lib/ticket-slot-prefill';
import { allPickable, buildCrewPickerLists, orphanSlotOption, pickerListsForSlot } from '../lib/crew-picker';
import { describeAiReplace } from '../lib/ai-suggest-replace';
import CasingStringMetresFields from '../components/casing/CasingStringMetresFields';
import { jobCasingStringsForDisplay } from '../lib/casing-strings-display';
import {
  buildCasingStringMetres, draftFromRecorded, readRecordedMetres, stringsThatTakeMetres, totalIsStringSum,
  type MetresDraft,
} from '../lib/ticket-string-metres';
import { frozenStampDisplay, frozenStampFromTicket } from '../lib/approval-stamp';
import {
  ArrowLeft,
  FileText,
  Calendar,
  Clock,
  Save,
  Send,
  Loader,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  DollarSign,
  Trash2,
  Plus,
  Info,
  Eye,
  Printer,
  Download,
  Mail,
  X,
  Mic,
  Wrench,
} from 'lucide-react';
import { Tooltip } from '../components/ui/tooltip';
import { priceBookLinkState, readDraftRateSheetItemId, readOffBook } from '../lib/price-book-link';


/**
 * A stored day for the editor's date fields, as 'YYYY-MM-DD', or `fallback`
 * when nothing is stored. An unreadable stored value is kept as it arrived:
 * the ticket is saved whole, and replacing it with the fallback would overwrite
 * a day the user never touched.
 */
function storedDayOr(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  return calendarDayString(value) ?? value;
}

// EquipmentUsageRow: lib/equipment-usage.ts, shared with the field editor.

interface LineItem extends DiscountFields {
  /**
   * The row's server id, or null/absent for a line not yet saved. Sent back so
   * the row-scoped save updates the row in place — a line that keeps its id
   * keeps its pay assignments.
   */
  id?: string | null;
  lineItemType: string;
  description: string;
  unit: string | null;
  rate: number | null;
  quantity: number | null;
  total: number | null;
  revenueCode: string | null;
  /**
   * The rate-sheet row the picker priced this line from (API 0406), or null
   * for a line typed by hand. It decides the QuickBooks item, so it must ride
   * along on every save — see lib/rate-sheet-items.ts, note 3. Editing the
   * description or rate never clears it; only re-picking a row replaces it.
   */
  rateSheetItemId: string | null;
  /**
   * 0504: the office's "deliberately not a price-book item". `undefined` is
   * "say nothing" and is never sent — see lib/price-book-link.ts for why a
   * missing column must not become `false`.
   */
  offBook?: boolean;
  assignedTech1Initials: string | null;
  assignedTech2Initials: string | null;
  assignedTech3Initials: string | null;
  assignedTech4Initials: string | null;
  // The authoritative slot. Initials above are kept beside it for display and
  // for older offline bundles — see lib/crew-slot.ts.
  assignedTech1Id: string | null;
  assignedTech2Id: string | null;
  assignedTech3Id: string | null;
  assignedTech4Id: string | null;
}

/** A server line row (either casing) as the editor's LineItem, id kept. */
function hydrateLineItems(rawLines: readonly any[]): LineItem[] {
  // Stamped with the base the server priced any Multi Rig line from, so its
  // total is the server's until a base line changes — lib/ticket-discount.
  return stampDiscountBase(rawLines.map((li: any) => ({
    id: li.id != null ? String(li.id) : null,
    lineItemType: toLineItemType(li.line_item_type || li.lineItemType) ?? 'other',
    description: li.description ?? '',
    unit: li.unit || null,
    rate: li.rate != null ? Number(li.rate) : null,
    quantity: li.quantity != null ? Number(li.quantity) : null,
    total: li.total != null ? Number(li.total) : null,
    revenueCode: li.revenue_code || li.revenueCode || null,
    rateSheetItemId: li.rate_sheet_item_id || li.rateSheetItemId || null,
    offBook: readOffBook(li),
    assignedTech1Initials: li.assigned_tech_1_initials || li.assignedTech1Initials || null,
    assignedTech2Initials: li.assigned_tech_2_initials || li.assignedTech2Initials || null,
    assignedTech3Initials: li.assigned_tech_3_initials || li.assignedTech3Initials || null,
    assignedTech4Initials: li.assigned_tech_4_initials || li.assignedTech4Initials || null,
    assignedTech1Id: li.assigned_tech_1_id || li.assignedTech1Id || null,
    assignedTech2Id: li.assigned_tech_2_id || li.assignedTech2Id || null,
    assignedTech3Id: li.assigned_tech_3_id || li.assignedTech3Id || null,
    assignedTech4Id: li.assigned_tech_4_id || li.assignedTech4Id || null,
    ...readDiscountFields(li),
  })));
}

/**
 * Coerce an incoming job_type into the canonical code the picker binds to.
 *
 * Form state holds CODES, never display text. This previously title-cased the
 * code into display text on prefill and the submit handler sent that text back
 * verbatim — which is how this column filled up with "Production" and
 * "Liner And Tie Back". With the enum in place that round-trip is a guaranteed
 * 400 on any prefilled save, so it must not come back.
 *
 * Anything not in the canonical vocabulary resolves to '' (the field is
 * optional) so the user is shown an empty picker instead of an invalid value
 * silently riding along on save.
 */
function toJobTypeCode(raw: string): string {
  return toJobType(raw) ?? '';
}

/** One id, so a second save's warning replaces the first and a new save can dismiss it. */
const PAY_ALREADY_PAID_TOAST_ID = 'pay-already-paid';
/** The approved-pay refusal's toast. Same lifetime as the paid warning: until dismissed or the next save. */
const PAY_LOCK_TOAST_ID = 'pay-lock-refused';

export default function NewTicketScreen() {
  // A new ticket's arrival / finish day pre-fills with the TENANT's today
  // (companies.time_zone), not the device's — an evening entry on a laptop left
  // on UTC used to pre-fill tomorrow.
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ticketId: editTicketId } = useParams<{ ticketId?: string }>();
  const isEditing = !!editTicketId;
  /*
   * Editing goes BACK one entry on save, to the ticket it was opened from.
   * It used to replace this entry with the list, so history read
   * list → ticket → list and the list's Back returned to the ticket. A new
   * ticket still replaces to the list: there is no ticket behind the form.
   */
  const goBack = useBack(isEditing ? `/tickets/${editTicketId}` : '/tickets');
  const jobIdParam = searchParams.get('jobId');
  const { company } = useAuth();
  const queryClient = useQueryClient();
  const currency = company?.country === 'CA' ? 'CAD' : 'USD';

  const [message, setMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDocError, setPreviewDocError] = useState<string | null>(null);
  /** Synchronous up to the tab claim — see lib/ticket-pdf.ts. */
  const previewPdf = (action: TicketPdfAction) => {
    if (!editTicketId) return;
    setPreviewDocError(null);
    void runTicketPdfAction(
      action,
      { id: editTicketId, status: existingTicket?.status, ticketNumber: existingTicket?.ticket_number ?? existingTicket?.ticketNumber },
      { onError: setPreviewDocError },
    );
  };
  // The print frame outlives the click (its dialog lives in it); leaving the
  // screen is when it goes.
  useEffect(() => () => disposePrintFrame(), []);
  // Payload held back while the revert confirm is open; null when no dialog.
  const [pendingRevertSave, setPendingRevertSave] = useState<any | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  /**
   * The line ids the server handed this editor, or null when it has none to
   * claim (a new ticket, or rows that arrived without ids). Null omits the key
   * and the API replaces the whole set; `[]` means "handed nothing, may add,
   * may delete nothing". Never collapse one into the other.
   */
  const [knownLineItemIds, setKnownLineItemIds] = useState<string[] | null>(null);
  /**
   * The AI prefill draft this editing session started from (API 0421), sent
   * back on the line save so the API can link the edit to the draft. Null
   * until AI Suggest succeeds; a save made without AI cites nothing.
   */
  const [aiDraftId, setAiDraftId] = useState<string | null>(null);
  /**
   * The job that draft was drafted for. The create cites the draft only while
   * the ticket's job is still that job: the API refuses a draft_id drafted for
   * a different job with a 400, and a refused id would fail the whole create.
   */
  const [aiDraftJobId, setAiDraftJobId] = useState<string | null>(null);
  // A list per row, not a string: the API can refuse one row for more than one
  // reason at a time and the office needs to see all of them, or it fixes the
  // rate, resubmits, and meets the same 400 about the quantity.
  const [lineItemErrors, setLineItemErrors] = useState<Record<number, RowErrorReason[]>>({});
  const [lineItemTypeErrors, setLineItemTypeErrors] = useState<Record<number, string>>({});
  /*
   * The rendered row per index, so a refusal can bring the offending line on
   * screen. A TNT ticket runs to fifteen rows and the marked one is routinely
   * below the fold, which turns a precise refusal back into "something failed".
   */
  const lineItemRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // The lines as the server last handed them (open, or a save's response) —
  // only read to tell "the hand removed only discounts" from "the hand removed
  // everything", which the office line door treats differently.
  const serverLinesRef = useRef<LineItem[]>([]);
  // The types this company hides from the picker. A row already typed as a
  // hidden value keeps its own option — see visibleLineItemTypeOptions.
  const { hidden: hiddenLineItemTypes } = useHiddenLineItemTypes();
  // Set when a picked price book row carried a unit this app cannot store — a
  // period rate like "5 day", which the canonical vocabulary has no way to
  // express. Rendered on the row, because the alternative to saying so is
  // resolving "5 day" to "per day" and billing a fifth of the work.
  const [lineItemUnitNotes, setLineItemUnitNotes] = useState<Record<number, string>>({});
  const [crew, setCrew] = useState<any[]>([]);
  // Which hand-added rows the job-position pre-fill may still suggest crew
  // for (lib/ticket-slot-prefill.ts). Keyed by row index like the unit notes,
  // and reset with them on every wholesale replacement of the rows — so a row
  // that arrived from the server or the AI draft is never in it and never
  // suggested over. 'open': eligible, nothing suggested yet. 'prefilled': the
  // slots hold a suggestion nobody has touched, shown as such. A person
  // picking any slot removes the row for good.
  const [slotPrefill, setSlotPrefill] = useState<Record<number, 'open' | 'prefilled'>>({});
  // AI Suggest is waiting on the user to agree to lose the lines already here.
  const [aiReplaceConfirm, setAiReplaceConfirm] = useState(false);
  const [aiReasoning, setAiReasoning] = useState<string | null>(null);
  const [aiConfidence, setAiConfidence] = useState<string | null>(null);
  // The sheets a dispatcher must choose between. Held separately from `message`
  // because that toast auto-dismisses after 5s, and a decision cannot live in
  // something that disappears — this banner stays until the next AI run.
  const [aiRateSheetChoice, setAiRateSheetChoice] = useState<AiDraftNotice['candidates']>([]);
  const [editLoaded, setEditLoaded] = useState(false);
  const [equipmentUsage, setEquipmentUsage] = useState<EquipmentUsageRow[]>([]);
  const [form, setForm] = useState({
    ticketNumber: '',
    // `?? ''` is an input seed only (the picker shows its placeholder); inside
    // the app the zone gate guarantees a zone, so this branch is not reached.
    arrivalDate: todayCalendarDate(timeZone) ?? '',
    arrivalTime: '',
    finishDate: todayCalendarDate(timeZone) ?? '',
    finishTime: '',
    customerName: '',
    rigName: '',
    lsd: '',
    metresRan: '',
    jobType: '',
    pipeSize: '',
    connections: '',
    notes: '',
    customerNotes: '',
  });

  // Fetch existing ticket for editing
  const { data: existingTicket, isLoading: editLoading } = useQuery<any>({
    queryKey: ['ticket-edit', editTicketId],
    // The envelope fallthrough here was the worst kind: it made `existingTicket`
    // a truthy object of undefined fields, so the prefill effect below ran and
    // produced a BLANK EDIT FORM over a real ticket — and Save would then write
    // those blanks back. null instead, guarded before the form renders.
    queryFn: () => api.tickets.get(editTicketId!).then(r => unwrap<any>(r.data)),
    enabled: isEditing,
  });

  const jobId = jobIdParam || existingTicket?.job_id || existingTicket?.jobId || null;

  // The customer's ink signature for the preview's stamp box. Requested only
  // while the preview is open and the frozen stamp says it has a signature.
  const previewStampSignature = useStampSignatureUrl(
    editTicketId,
    showPreview && frozenStampFromTicket(existingTicket)?.has_signature === true,
  );

  // Whether "Submit & Field Approve" is a real option on THIS ticket, judged on
  // its stored status. A new ticket is always submittable — it is created as a
  // draft by the same click. An existing one only from draft/rejected, which is
  // the API's own guard; every other status answers 409 and used to do so on a
  // save that had already succeeded.
  const canSubmit = !isEditing || ticketCanSubmit(existingTicket?.status);

  // Hours are the server's answer, never ours. `hoursAreStale` covers the gap
  // between editing a date/time and saving, when the stored number no longer
  // describes what's on screen.
  const serverHours = formatTicketHours(existingTicket?.total_hours ?? existingTicket?.totalHours);
  const timesContradict = hasContradictoryTimes(
    form.arrivalDate, form.finishDate, form.arrivalTime, form.finishTime
  );
  const hoursAreStale = !!existingTicket && ticketTimeKey(
    existingTicket.work_date ?? existingTicket.workDate,
    existingTicket.end_date ?? existingTicket.endDate,
    existingTicket.start_time ?? existingTicket.startTime,
    existingTicket.end_time ?? existingTicket.endTime,
  ) !== ticketTimeKey(form.arrivalDate, form.finishDate, form.arrivalTime, form.finishTime);

  // Pre-populate form from existing ticket
  useEffect(() => {
    if (!existingTicket || editLoaded) return;
    setEditLoaded(true);
    const t = existingTicket;
    setForm(f => ({
      ...f,
      ticketNumber: t.ticket_number || t.ticketNumber || f.ticketNumber,
      arrivalDate: storedDayOr(t.work_date || t.workDate, f.arrivalDate),
      arrivalTime: t.start_time || t.startTime || f.arrivalTime,
      finishTime: t.end_time || t.endTime || f.finishTime,
      finishDate: storedDayOr(t.end_date || t.endDate || t.work_date || t.workDate, f.finishDate),
      customerName: t.customer_name || t.customerName || f.customerName,
      rigName: t.rig_name || t.rigName || f.rigName,
      lsd: t.lsd || f.lsd,
      metresRan: t.actual_metres_run || t.actualMetresRun ? String(t.actual_metres_run || t.actualMetresRun) : f.metresRan,
      jobType: toJobTypeCode(t.job_type || t.jobType || ''),
      pipeSize: t.casing_size_inches || t.casingSizeInches ? `${t.casing_size_inches || t.casingSizeInches}"` : f.pipeSize,
      connections: t.thread_type || t.threadType || f.connections,
      notes: t.notes || f.notes,
      customerNotes: t.customer_notes || t.customerNotes || f.customerNotes,
    }));
    const rawLines = Array.isArray(t.lineItems) ? t.lineItems : (Array.isArray(t.line_items) ? t.line_items : []);
    if (rawLines.length > 0) {
      // Row notes are keyed by index, so a wholesale replacement of the rows
      // invalidates every one of them.
      setLineItemUnitNotes({});
      setSlotPrefill({});
      serverLinesRef.current = hydrateLineItems(rawLines);
      setLineItems(serverLinesRef.current);
    }
    /*
     * The baseline for the row-scoped save: the ids the server handed us.
     * Claimed only when EVERY row carried one — `.filter(Boolean)` over rows
     * with no id would produce `[]`, which says "handed nothing" and makes
     * every line undeletable. `null` omits the key and the API replaces the
     * whole set, which is what those rows were written under. An empty ticket
     * yields `[]`, correctly.
     */
    const handedIds = rawLines.map((li: any) => (li?.id != null ? String(li.id) : null));
    setKnownLineItemIds(handedIds.every((id: string | null) => id) ? (handedIds as string[]) : null);
    if (Array.isArray(t.crew)) setCrew(t.crew);
  }, [existingTicket, editLoaded]);

  const { data: job, isLoading: jobLoading } = useQuery<any>({
    queryKey: ['job-for-ticket', jobId],
    queryFn: () => api.jobs.get(jobId!).then(r => r.data.data ?? r.data),
    enabled: !!jobId,
  });

  // ─── Metres run per casing string (api#830) ───────────────────────────────
  // EDIT ONLY: ticket create does not accept per-string metres, so a new
  // ticket records them on its first save. The total is the API's
  // actual_metres_run — see lib/ticket-string-metres.
  const metresStrings = isEditing ? stringsThatTakeMetres(jobCasingStringsForDisplay(job)) : [];
  const recordedMetres = readRecordedMetres(existingTicket);
  const [metresDraft, setMetresDraft] = useState<MetresDraft>({});
  useEffect(() => {
    if (existingTicket) setMetresDraft(draftFromRecorded(readRecordedMetres(existingTicket)));
  }, [existingTicket]);

  const { data: nextNumberData } = useQuery<any>({
    queryKey: ['ticket-next-number'],
    queryFn: () => api.tickets.getNextNumber().then(r => r.data.data ?? r.data),
  });

  // ─── The price book behind the Description field ──────────────────────────
  //
  // Scoped by CUSTOMER, not by the job's rate sheet. `customer_id` widens — that
  // customer's own bid contracts plus the company-wide books — which is exactly
  // the set that may be billed on this ticket, and it keeps another customer's
  // contract out. `jobs.rate_sheet_id` only hoists the matching group inside the
  // menu; filtering on it would empty the picker on every job in production,
  // where the column is NULL on all of them by design.
  //
  // Read straight from GET /rate-sheets/items rather than from the `billing`
  // payload this screen already has in hand on `job`. `billing` is null when the
  // job has no customer, is not scoped by the job's sheet either, and — the
  // deciding reason — has no counterpart on the field editor's job endpoint. Two
  // sources would put the two editors back on separate code paths, which is how
  // they became copies of each other in the first place.
  const isOnline = useSyncStore((s) => s.isOnline);
  const ticketCustomerId: string | undefined =
    job?.customer_id ?? job?.customerId ?? undefined;
  const jobRateSheetId: string | null =
    job?.rate_sheet_id ?? job?.rateSheetId ?? null;

  const {
    data: rateSheetItems,
    isLoading: rateItemsLoading,
    isError: rateItemsFailed,
  } = useQuery<RateSheetItem[]>({
    queryKey: ['rate-sheet-items', ticketCustomerId ?? null],
    queryFn: () =>
      api.rateSheets.items({ customer_id: ticketCustomerId }).then(r => unwrapArray<RateSheetItem>(r.data)),
    enabled: isOnline && !!ticketCustomerId,
    // Prices change on the order of weeks, and a hand adds several lines in a
    // row. One fetch per editing session is plenty.
    staleTime: 5 * 60_000,
    retry: false,
  });

  // `jobLoading` comes FIRST among the reasons, because the customer id is read
  // off the job: without it, a job still in flight reports "no client on this
  // job" — a settled answer, given before the question could be asked.
  const rateItemsState: RateSheetItemsState =
    !isOnline ? 'offline'
    : jobLoading ? 'loading'
    : !ticketCustomerId ? 'no-customer'
    : rateItemsFailed ? 'failed'
    : rateItemsLoading ? 'loading'
    : (rateSheetItems?.length ?? 0) === 0 ? 'empty'
    : 'ready';

  useEffect(() => {
    if (job && !isEditing) {
      const rawSchedDate = job.scheduled_date ?? job.scheduledDate ?? '';
      const schedDate = rawSchedDate ? (calendarDayString(rawSchedDate) ?? rawSchedDate) : '';
      const rawJobType = job.job_type ?? job.jobType ?? job.type ?? '';
      setForm(f => ({
        ...f,
        customerName: job.customer_name ?? job.customerName ?? job.customer ?? '',
        rigName: job.rig_name ?? job.rigName ?? job.rig ?? '',
        lsd: job.lsd ?? job.well_id_lsd ?? '',
        arrivalDate: schedDate || f.arrivalDate,
        finishDate: schedDate || f.finishDate,
        jobType: toJobTypeCode(rawJobType),
        pipeSize: job.casing_size_inches ? `${job.casing_size_inches}"` : (job.casingSize ?? ''),
        connections: job.thread_type ?? job.threadType ?? '',
      }));
      // Pre-load crew from job
      if (Array.isArray(job.crew) && job.crew.length > 0) {
        setCrew(job.crew);
      }
    }
  }, [job]);

  // EDIT MODE gets the job crew too. GET /tickets/:id answers with the job's
  // roster under `crew` (tickets.routes.ts getTicketWithDetails), and the
  // hydrate above reads it — but a ticket whose payload carried none (an older
  // API, a job the ticket was moved onto) left the Tech 1-4 picker empty, with
  // the job's roster one query away. The job fetch already runs in edit mode
  // for the price book; this only lets its crew through when nothing else has.
  useEffect(() => {
    if (!job || !isEditing || crew.length > 0) return;
    if (Array.isArray(job.crew) && job.crew.length > 0) setCrew(job.crew);
  }, [job, isEditing, crew.length]);

  // The job crew is the default list, not a closed one — a hand picked up
  // mid-shift is on the ticket before anyone has put them on the roster.
  // Everyone else at the company goes under a second heading in the picker,
  // but only once asked for: off on every open, and the switch is what turns
  // on the fetch. Mirrors FieldTicketsScreen.
  const [showEveryone, setShowEveryone] = useState(false);
  const { data: companyUsers } = useQuery<any[]>({
    queryKey: ['crew-picker-users'],
    queryFn: () => api.users.list().then(r => unwrapArray<any>(r.data)),
    enabled: isOnline && showEveryone,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const crewLists = useMemo(() => buildCrewPickerLists(crew, companyUsers), [crew, companyUsers]);

  // Pre-load equipment usage from job
  useEffect(() => {
    if (!job || isEditing) return;
    const eq: any[] = job.equipment ?? [];
    if (eq.length > 0 && equipmentUsage.length === 0) {
      setEquipmentUsage(eq.map(seedEquipmentUsageRow));
    }
  }, [job, isEditing, equipmentUsage.length]);

  // AI Prefill
  const aiMutation = useMutation({
    mutationFn: (forJobId: string) => api.tickets.aiPrefill(forJobId),
    onSuccess: (res, forJobId) => {
      const draft = res.data.data ?? res.data;
      // Use the actual metres ran from the form (user input) instead of AI's job depth
      const actualMetres = form.metresRan ? parseFloat(form.metresRan) : (draft.depthMetres ? Number(draft.depthMetres) : null);
      // Ensure totals are calculated and units normalized for each line item
      const items = (draft.lineItems ?? []).map((li: LineItem) => {
        const rate = li.rate != null ? Number(li.rate) : null;
        const unit = normalizeUnit(li.unit);
        // For per-metre items, use the actual metres ran instead of AI-suggested depth
        const qty = (unit === 'per_metre' && actualMetres) ? actualMetres : (li.quantity != null ? Number(li.quantity) : null);
        const total = (rate != null && qty != null) ? Math.round(rate * qty * 100) / 100 : null;
        return { ...li, rate, quantity: qty, total, unit };
      });
      setLineItemUnitNotes({});
      setSlotPrefill({});
      // Same service as a line already on the ticket → keep that line's id, so
      // the save updates it in place instead of re-inserting it (which orphans
      // its pay). See lib/ai-draft-lines.
      // A discount is not a service line the draft could suggest, so replacing
      // the lines with the draft keeps the ticket's discounts, after the draft.
      setLineItems([...carryExistingLineIds<LineItem>(items, lineItems.filter(l => !isDiscountLine(l))), ...lineItems.filter(isDiscountLine)]);
      // Row marks are keyed by index and every row has just moved.
      setLineItemErrors({});
      setLineItemTypeErrors({});
      setAiDraftId(readPrefillDraftId(draft));
      setAiDraftJobId(forJobId);
      if (Array.isArray(draft.crew) && draft.crew.length > 0) {
        setCrew(draft.crew);
      }
      setAiReasoning(draft.reasoning ?? null);
      setAiConfidence(draft.confidence ?? null);
      // Also update metresRan if not already set
      setForm(f => ({
        ...f,
        customerName: draft.customerName ?? f.customerName,
        rigName: draft.rigName ?? f.rigName,
        lsd: draft.lsd ?? f.lsd,
        jobType: draft.jobType ? toJobTypeCode(draft.jobType) : f.jobType,
        pipeSize: draft.casingSizeInches ? `${draft.casingSizeInches}"` : f.pipeSize,
        notes: draft.notes ?? f.notes,
        customerNotes: draft.customerNotes ?? draft.customer_notes ?? f.customerNotes,
        metresRan: f.metresRan || (draft.depthMetres ? String(draft.depthMetres) : f.metresRan),
      }));
      // The draft comes back with a full set of line items even when NOTHING
      // could price them — the API prompt requires NEVER skip a service — so
      // the line-item count alone says nothing about whether pricing worked.
      // Read the reason instead. See lib/rate-sheet-resolution.ts.
      const notice = describeAiDraft(
        draft.lineItems?.length ?? 0,
        draft.confidence,
        readRateSheetResolution(draft)
      );
      setAiRateSheetChoice(notice.candidates);
      setMessage({ type: notice.tone, text: notice.text });
      // A warning names an unfinished task, so it does not time itself out.
      if (notice.tone === 'success') setTimeout(() => setMessage(null), 5000);
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'AI prefill failed — is ANTHROPIC_API_KEY set?' });
    },
  });

  const [voiceTranscript, setVoiceTranscript] = useState<string | null>(null);

  // Voice Input
  const voiceMutation = useMutation({
    mutationFn: (audioBlob: Blob) => api.tickets.voiceInput(jobId!, audioBlob),
    onSuccess: (res) => {
      const draft = res.data.data ?? res.data;
      setVoiceTranscript(draft.transcript ?? null);
      setAiReasoning(draft.reasoning ?? null);
      setAiConfidence(draft.confidence ?? null);
      // The voice draft shares gatherJobContext, so it hits the SAME ambiguity
      // and reports it in the same field. Setting this unconditionally also
      // clears a banner left over from an earlier AI Suggest on this screen —
      // a stale "pick one" pointing at a draft the dictation just replaced
      // would be worse than the silence this PR is fixing.
      setAiRateSheetChoice(
        describeAiDraft(
          draft.lineItems?.length ?? 0,
          draft.confidence,
          readRateSheetResolution(draft)
        ).candidates
      );
      // Pre-fill line items if returned (ensure totals calculated + units normalized)
      if (draft.lineItems?.length > 0) {
        setLineItemUnitNotes({});
        setSlotPrefill({});
        // Keeps the ticket's discounts, after the drafted lines, for the same
        // reason AI Suggest does: every plain line now sends discount_kind,
        // which makes the save discount-aware, so a discount left out of this
        // list would be deleted by the server on a save that reports success.
        setLineItems(items => [...draft.lineItems.map((li: LineItem) => {
          const rate = li.rate != null ? Number(li.rate) : null;
          const qty = li.quantity != null ? Number(li.quantity) : null;
          const total = li.total != null ? Number(li.total)
            : (rate != null && qty != null ? Math.round(rate * qty * 100) / 100 : null);
          // The API links a drafted line when exactly one item on the resolved
          // sheet has its name (0504, decided in code, not by the model), and
          // sends nothing otherwise. Kept as sent; nulling it unlinked them all.
          return { ...li, rate, quantity: qty, total, unit: normalizeUnit(li.unit), rateSheetItemId: readDraftRateSheetItemId(li) };
        }), ...items.filter(isDiscountLine)]);
      }
      // Pre-fill form fields
      setForm(f => ({
        ...f,
        customerName: draft.customerName ?? f.customerName,
        rigName: draft.rigName ?? f.rigName,
        lsd: draft.lsd ?? f.lsd,
        arrivalTime: draft.startTime ?? f.arrivalTime,
        finishTime: draft.endTime ?? f.finishTime,
        arrivalDate: draft.workDate ? storedDayOr(draft.workDate, f.arrivalDate) : f.arrivalDate,
        metresRan: draft.actualMetresRun ? String(draft.actualMetresRun) : f.metresRan,
        pipeSize: draft.casingSizeInches ? `${draft.casingSizeInches}"` : f.pipeSize,
        connections: draft.threadType ?? f.connections,
        notes: draft.notes ?? f.notes,
        customerNotes: draft.customerNotes ?? draft.customer_notes ?? f.customerNotes,
      }));
      setMessage({ type: 'success', text: `Voice processed: ${draft.lineItems?.length ?? 0} line items (${draft.confidence} confidence)` });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'Voice processing failed — is OPENAI_API_KEY set?' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      if (isEditing) {
        // One key per DB column, camelCase only. Two spellings of the same
        // field (or a derived value the server also derives) make Postgres
        // raise 42601 "multiple assignments to same column".
        //
        // Deliberately NOT sent:
        //   totalHours    — the server recomputes total_hours from
        //                   workDate/endDate/startTime/endTime. Sending ours
        //                   too was the second assignment to total_hours.
        //   status        — PUT rejects status changes; transitions go through
        //                   the dedicated endpoints (submit below).
        //   ticketNumber, id, companyId, updatedAt — not editable here.
        const fields: Record<string, any> = {
          workDate: data.workDate,
          endDate: data.endDate,
          startTime: data.startTime,
          endTime: data.endTime,
          customerName: data.customerName,
          rigName: data.rigName,
          lsd: data.lsd,
          jobType: data.jobType,
          casingSizeInches: data.casingSizeInches,
          threadType: data.threadType,
          actualMetresRun: data.actualMetresRun,
          notes: data.notes ?? '',
          customerNotes: data.customerNotes ?? '',
          equipmentUsage: data.equipmentUsage,
          casingStringMetres: data.casingStringMetres,
        };
        // Blank optional inputs arrive as undefined — omit them rather than
        // clearing stored values. Cleared text areas send '' (server → NULL).
        for (const key of Object.keys(fields)) {
          if (fields[key] === undefined) delete fields[key];
        }
        // The paid-pay warning (api#1352) can ride on either write: the date
        // door or the line door. Collected from both, and shown once.
        const paidWarnings: string[] = [];
        // A later call in this save can still fail after an earlier one saved
        // on paid pay. The warning rides on the error then, or onError would
        // show only the failure and the saved edit's instruction would be lost.
        const keepPaid = (e: any) => {
          if (paidWarnings.length > 0 && e && typeof e === 'object') e.paidWarnings = [...paidWarnings];
          return e;
        };
        if (Object.keys(fields).length > 0) {
          const updated = await api.tickets.update(editTicketId!, fields);
          const w = payAlreadyPaidMessage(updated?.data);
          if (w) paidWarnings.push(w);
        }
        /*
         * An empty list is still not sent — the office line door refuses one —
         * with ONE exception: every line the server last handed us was a
         * discount, so the hand has removed only discounts. Then `[]` with
         * discountAware is the only way to say so; skipping the call would
         * report a save and leave the discount on the ticket.
         */
        const onlyDiscountsCleared =
          !(data.lineItems?.length > 0) &&
          serverLinesRef.current.length > 0 &&
          serverLinesRef.current.every(isDiscountLine);
        if (data.lineItems?.length > 0 || onlyDiscountsCleared) {
          const res = await api.tickets.updateLineItems(editTicketId!, data.lineItems ?? [], {
            // `?? undefined`, never `?? []` — see knownLineItemIds.
            knownLineItemIds: knownLineItemIds ?? undefined,
            draftId: aiDraftId ?? undefined,
            discountAware: true,
          }).catch((e: any) => { throw keepPaid(e); });
          // Re-seed from what committed. A later call in this save (submit) can
          // still fail and leave the editor open; a second save must not resend
          // the new rows without their ids, which the row-scoped door would
          // insert a second time.
          const lineWarning = payAlreadyPaidMessage(res?.data);
          if (lineWarning && !paidWarnings.includes(lineWarning)) paidWarnings.push(lineWarning);
          const committed = res?.data?.data;
          if (Array.isArray(committed)) {
            const ids = committed.map((li: any) => (li?.id != null ? String(li.id) : null));
            setKnownLineItemIds(ids.every((id: string | null) => id) ? (ids as string[]) : null);
            setLineItemUnitNotes({});
            setSlotPrefill({});
            serverLinesRef.current = hydrateLineItems(committed);
            setLineItems(serverLinesRef.current);
          }
        }
        // "Submit & Field Approve" is a status transition, not a field edit.
        // Only reached from draft/rejected — handleSubmit refuses to set
        // `submitted` on anything else (`canSubmit`). It used to key on the
        // button alone, so editing an office_approved ticket ran this against a
        // ticket the PUT above had just reverted to field_approved, and the
        // 409 turned a working save into a red banner.
        if (data.status === 'submitted') {
          await api.tickets.submit(editTicketId!).catch((e: any) => { throw keepPaid(e); });
        }
        return { data: { id: editTicketId }, paidWarnings };
      }
      return api.tickets.create(data);
    },
    // The pre-save status has to be captured BEFORE the write. Reading it off
    // `existingTicket` in onSuccess worked only by accident: onSuccess now runs
    // after invalidateTickets has marked ['ticket-edit'] stale, and the closure
    // it read was whatever the last render happened to hold.
    onMutate: () => {
      // A new save is the end of the last save's paid-pay warning.
      toast.dismiss(PAY_ALREADY_PAID_TOAST_ID);
      toast.dismiss(PAY_LOCK_TOAST_ID);
      return { preSaveStatus: existingTicket?.status ?? null };
    },
    // Invalidation belongs to the WRITE, not to the mutation's verdict. The
    // save is several calls deep; the first ones can land and a later one still
    // reject, and when it did (a stray /submit answering 409) onSuccess never
    // ran, so the editor kept serving the pre-save ticket from cache while the
    // row on disk was already correct. The header rendered a stale updated_at
    // and "editor not recorded" over a save that had worked.
    //
    // onSettled runs on both paths, so what actually reached the server reaches
    // the screen either way. The user-facing verdict — message, revert toast,
    // navigate — stays in onSuccess, where a failure must not produce it.
    onSettled: () => {
      invalidateTickets(queryClient);
    },
    onSuccess: (result: any, _variables, context) => {
      /*
       * SAVED, BUT THE PAY WAS ALREADY PAID (api#1352). The edit is on the
       * ticket and payroll will not change, so this is not a success tick. It
       * is the API's sentence, verbatim, as a warning.
       *
       * It is a toast with no timeout because this screen navigates away 1.2s
       * after a save, and an in-page banner would leave with it. The toast
       * lives in the app shell, so it survives that. It stays until it is
       * dismissed or the next save starts (onMutate above).
       */
      const paid: string[] = Array.isArray(result?.paidWarnings) ? result.paidWarnings : [];
      if (paid.length > 0) {
        setMessage({ type: 'warning', text: paid.join(' ') });
        toast.warning(paid.join(' '), { id: PAY_ALREADY_PAID_TOAST_ID, duration: Infinity, closeButton: true });
      } else {
        setMessage({ type: 'success', text: isEditing ? 'Ticket updated!' : 'Ticket created!' });
      }
      // Only an OFFICE-APPROVED ticket is changed by being edited. The old
      // message fired for every approved-or-later status and named the wrong
      // target on all of them: it claimed "reset to Draft" when the API reverts
      // to field_approved, and it claimed it about field_approved edits, which
      // change no status at all. customer_approved and invoiced never reach
      // here — the API refuses those with a 409 surfaced in onError below.
      if (isEditing && ticketEditReverts(context?.preSaveStatus)) {
        toast.warning(
          `Saved — this ticket is back to ${officeTicketStatusLabel('field_approved')} and needs office approval again. ` +
          'The approval link sent to the customer no longer works; send it again once re-approved.',
          { duration: 8000 }
        );
      }
      setTimeout(() => {
        if (isEditing) goBack();
        else navigate('/tickets', { replace: true });
      }, 1200);
    },
    onError: (err: any) => {
      const resData = err.response?.data;
      // An earlier call in this save landed on paid pay (see keepPaid). The
      // failure below is still the banner; the paid instruction is the toast.
      if (Array.isArray(err?.paidWarnings) && err.paidWarnings.length > 0) {
        toast.warning(err.paidWarnings.join(' '), { id: PAY_ALREADY_PAID_TOAST_ID, duration: Infinity, closeButton: true });
      }
      // Surface per-row validation errors from the API's 400 details[]
      // Only when the refusal names rows. A 400 with an empty details[] (the
      // discount door's "this save would remove every line…") marks nothing, so a
      // banner pointing at marked lines would point at none; its own sentence
      // is shown instead, by the branch below.
      if (err.response?.status === 400 && Array.isArray(resData?.details) && resData.details.length > 0) {
        const grouped = groupRowErrors(resData.details);
        setLineItemErrors(grouped);
        /*
         * The banner is built from the rows, NOT read off `resData.error`.
         *
         * That string is the constant "Invalid line items" for every line-item
         * 400 the API can raise — a category, not a finding, and it does not
         * send the reader down to the rows this same handler has just marked.
         * The local refusal has always pointed downward; one defect was
         * getting two sentences depending on which side caught it.
         */
        setMessage({ type: 'error', text: bannerForRowErrors(grouped) });
        // Same treatment a local refusal gets — the marked row is often below
        // the fold on a long ticket.
        const first = Object.keys(grouped).map(Number).sort((a, b) => a - b)[0];
        if (first != null) scrollRowIntoView(lineItemRowRefs.current[first]);
      } else if (isPayLockRefusal(resData) && typeof resData?.error === 'string') {
        /*
         * REFUSED BECAUSE PAY IS APPROVED (api#1352). The API's sentence,
         * verbatim: it already names the remedy ("unapprove pay", or "reopen
         * the pay period, then unapprove pay").
         *
         * Also a toast, because the banner is at the top of a long form and
         * Save is at the bottom. Measured on Smith at 390px: the banner was
         * off-screen when the refusal arrived, so the save just looked like it
         * did nothing. It stays until it is dismissed or the next save starts.
         */
        setMessage({ type: 'error', text: resData.error });
        toast.error(resData.error, { id: PAY_LOCK_TOAST_ID, duration: Infinity, closeButton: true });
      } else {
        setMessage({ type: 'error', text: resData?.error ?? (isEditing ? 'Failed to update ticket' : 'Failed to create ticket') });
      }
    },
  });

  const handleSubmit = (asDraft = true) => {
    if (!form.arrivalDate || !form.arrivalTime || !form.finishTime) {
      setMessage({ type: 'error', text: 'Arrival date, arrival time, and finish time are required' });
      return;
    }

    /*
     * Every line owes a description, a type, a rate and a quantity, and the
     * same helper decides that for the field editor — see
     * lib/line-item-validation. Written out here twice before, and the two
     * copies had already begun to differ.
     */
    const lineCheck = checkLineItems(lineItems);
    setLineItemErrors(lineCheck.rowErrors);
    setLineItemTypeErrors(lineCheck.typeErrors);
    if (!lineCheck.ok) {
      setMessage({ type: 'error', text: lineCheck.message! });
      if (lineCheck.firstInvalidIndex != null) {
        scrollRowIntoView(lineItemRowRefs.current[lineCheck.firstInvalidIndex]);
      }
      return;
    }

    const usageErr = equipmentUsageError(equipmentUsage);
    if (usageErr) {
      setMessage({ type: 'error', text: usageErr });
      return;
    }

    const stringMetres = buildCasingStringMetres(metresStrings, recordedMetres, metresDraft);
    if (!stringMetres.ok) {
      setMessage({ type: 'error', text: stringMetres.error });
      return;
    }

    // total_hours is computed server-side from workDate/endDate/startTime/
    // endTime on every write (API #194). Nothing is computed or sent here —
    // a client number would only drift from the stored one.
    const payload = {
      ticketNumber: form.ticketNumber || undefined,
      jobId,
      customerId: job?.customer_id ?? job?.customerId,
      // `submitted` here is not a stored status — it is this payload's way of
      // asking mutationFn for the POST /submit transition after the write. It
      // is only ever set when the API will accept that transition, so the
      // request is never sent to be refused: `canSubmit` is false for every
      // existing ticket outside draft/rejected. Guarded here rather than at the
      // buttons because six call sites reach this function.
      status: isEditing
        ? (asDraft || !canSubmit ? undefined : 'submitted')
        : (asDraft ? 'draft' : 'submitted'),
      workDate: form.arrivalDate,
      endDate: form.finishDate,
      startTime: form.arrivalTime,
      endTime: form.finishTime,
      customerName: form.customerName,
      rigName: form.rigName,
      lsd: form.lsd,
      jobType: form.jobType || undefined,
      casingSizeInches: form.pipeSize ? parseFloat(form.pipeSize) : undefined,
      threadType: form.connections || undefined,
      actualMetresRun: form.metresRan ? parseFloat(form.metresRan) : undefined,
      notes: form.notes,
      customerNotes: form.customerNotes || undefined,
      // Shown lines (a Multi Rig line carries its estimate, which the server
      // ignores) as the wire shape: discount keys snake_case, no crew on a
      // discount, client-only keys dropped. A plain line is sent unchanged.
      lineItems: shownLines.length > 0 ? shownLines.map(lineForWire) : undefined,
      crew: crew.length > 0 ? crew : undefined,
      // hours/runs keys only when typed — lib/equipment-usage.ts.
      equipmentUsage: equipmentUsage.length > 0 ? serializeEquipmentUsage(equipmentUsage) : undefined,
      // API 0422: the prefill this new ticket was written from, so its
      // corrections can be learned from. Create only — an edit cites the draft
      // on the line save instead. Absent when AI Suggest did not run.
      draftId: isEditing ? undefined : draftIdForCreate(aiDraftId, aiDraftJobId, jobId),
      // Edit only (create does not take it). No key when there is nothing to say.
      casingStringMetres: isEditing && stringMetres.entries.length > 0 ? stringMetres.entries : undefined,
    };

    // Saving an edit to an OFFICE-APPROVED ticket is not a plain save: the API
    // reverts the ticket, reverses its cert days and kills the customer's
    // outstanding approval link (API #311). Confirm before that happens, not
    // after. Gated here rather than at each button because six call sites reach
    // this function — a check on the buttons would miss one, and the one it
    // missed would be the silent path.
    //
    // Every other editable status saves in place, so no dialog: warning about a
    // revert that will not happen trains people to click through the one that
    // will.
    if (ticketEditReverts(existingTicket?.status)) {
      setPendingRevertSave(payload);
      return;
    }

    createMutation.mutate(payload);
  };

  /**
   * Pick a tech for one slot. Writes the id (authoritative) AND the initials
   * (display, and what an older offline bundle reads) in one state update, so
   * the two can never disagree on a row.
   */
  const setLineItemTech = (index: number, slot: 1 | 2 | 3 | 4, userId: string | null) => {
    const member = findCrewById(allPickable(crewLists), userId);
    setLineItems(items => items.map((item, i) => i !== index ? item : assignTechSlot(item, slot, userId, member)));
    // A person has answered for this row; the suggestion mark comes off and
    // nothing is suggested on it again.
    setSlotPrefill(prev => { if (!(index in prev)) return prev; const next = { ...prev }; delete next[index]; return next; });
  };

  /**
   * The job-position suggestion for one row (lib/ticket-slot-prefill.ts). A row
   * still holding an untouched suggestion has it taken back first, so changing
   * the type re-suggests rather than leaving the last type's crew on the line;
   * a row a person has filled is not in `slotPrefill` and never reaches here.
   * No phase: tickets record none (see the helper).
   */
  const suggestSlots = (row: LineItem, mark: 'open' | 'prefilled') =>
    prefillTechSlots(mark === 'prefilled' ? clearTechSlots(row) : row, crew);

  /**
   * AI Suggest replaces the whole line item array when the draft lands (see
   * aiMutation). With rows already on the ticket that is a loss the user has
   * to agree to first — and be told the size of. With none, no dialog: the
   * normal path stays one click. Cancel fires no request.
   */
  // Discounts are kept across AI Suggest (see its onSuccess), so they are not
  // among the lines the dialog warns will be replaced.
  const aiReplaceCopy = describeAiReplace(lineItems.filter(l => !isDiscountLine(l)).length);
  const requestAiSuggest = () => {
    if (aiReplaceCopy) {
      setAiReplaceConfirm(true);
      return;
    }
    aiMutation.mutate(jobId!);
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const mark = slotPrefill[index];
    if (field === 'lineItemType' && mark && lineItems[index]) {
      // Computed out here, not in an updater: it also sets the mark, and an
      // updater runs twice under StrictMode.
      const { line, prefilled } = suggestSlots({ ...lineItems[index], lineItemType: value }, mark);
      setLineItems(items => items.map((row, i) => (i === index ? line : row)));
      setSlotPrefill(prev => ({ ...prev, [index]: prefilled ? 'prefilled' : 'open' }));
    } else {
      setLineItems(items => items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === 'rate' || field === 'quantity') {
          const r = field === 'rate' ? value : item.rate;
          const q = field === 'quantity' ? value : item.quantity;
          updated.total = r != null && q != null ? Math.round(Number(r) * Number(q) * 100) / 100 : null;
        }
        return updated;
      }));
    }
    /*
     * The mark comes off the moment the box has an answer in it — including a
     * typed 0, which `value != null` accepts and a truthiness test would not.
     * Scoped to the field that changed: a row can be refused for two things at
     * once, and clearing the whole row when the rate is filled would erase a
     * live description refusal and let the line read as corrected.
     */
    if ((field === 'description' || field === 'rate' || field === 'quantity') && value != null && value !== '') {
      setLineItemErrors(prev => clearFieldError(prev, index, field));
    }
    if (field === 'lineItemType' && lineItemTypeErrors[index]) {
      setLineItemTypeErrors(prev => { const next = { ...prev }; delete next[index]; return next; });
    }
    // The note says the picked unit could not be set automatically. Setting one
    // by hand is the thing it asks for, so it stops asking.
    if (field === 'unit' && lineItemUnitNotes[index]) {
      setLineItemUnitNotes(prev => { const next = { ...prev }; delete next[index]; return next; });
    }
  };

  /**
   * Fill this row from a price book item: Description, Rate, Revenue Code, and
   * Unit where the book's unit can be represented at all.
   *
   * TYPE IS NOT SET, deliberately — the rate sheet's item_type vocabulary and
   * the ticket's line_item_type enum are disjoint by design, and the row's own
   * "Type is required" check makes the hand choose. See lib/rate-sheet-items.ts.
   */
  // Off book on: the line is deliberately not a book item, so it holds no link
  // (a link and offBook together are a 400 on every write door). Off: a plain
  // unlinked line again. Never a block either way — see lib/price-book-link.
  const setLineOffBook = (index: number, offBook: boolean) => {
    setLineItems(items => items.map((row, i) => (
      i !== index ? row : offBook ? { ...row, offBook: true, rateSheetItemId: null } : { ...row, offBook: false }
    )));
  };

  // An exact name match on leaving the field links the line and nothing else:
  // the rate, unit and description stay as typed.
  const autoLinkLine = (index: number, item: RateSheetItem) => {
    setLineItems(items => items.map((row, i) => (
      i === index && !row.rateSheetItemId && row.offBook !== true ? { ...row, rateSheetItemId: item.id } : row
    )));
  };

  const pickRateSheetItem = (index: number, item: RateSheetItem) => {
    const current = lineItems[index];
    if (!current) return;
    // Applied once, out here — an updater that also assigns to something outside
    // itself runs twice under StrictMode and is the wrong place for the note.
    const { next: filled, unitNote } = applyRateSheetItem(current, item);
    setLineItems(items => items.map((row, i) => (i === index ? filled : row)));
    setLineItemUnitNotes(prev => {
      const next = { ...prev };
      if (unitNote) next[index] = unitNote; else delete next[index];
      return next;
    });
    // Field-scoped, because the price book fills a description and a rate but
    // never a quantity — clearing the whole row would take the mark off a
    // quantity box that is still empty.
    setLineItemErrors(prev => {
      let next = prev;
      if (filled.description?.trim()) next = clearFieldError(next, index, 'description');
      if (filled.rate != null) next = clearFieldError(next, index, 'rate');
      return next;
    });
  };

  const removeLineItem = (index: number) => {
    setLineItems(items => items.filter((_, i) => i !== index));
    // All three of these are keyed by ROW INDEX, so deleting a row shifts every
    // row after it and leaves its message pointing at the wrong line. Previously
    // only the two validation maps did this, and the red ring landing on an
    // innocent row read as a second bad line rather than a stale marker.
    setLineItemErrors(prev => reindexAfterRemoval(prev, index));
    setLineItemTypeErrors(prev => reindexAfterRemoval(prev, index));
    setLineItemUnitNotes(prev => reindexAfterRemoval(prev, index));
    setSlotPrefill(prev => reindexAfterRemoval(prev, index));
  };

  const addLineItem = () => {
    const blank: LineItem = {
      // Deliberately unset — the operator picks a type. Defaulting to 'other'
      // silently sent every manual line down the AI-guess pay path.
      lineItemType: '',
      description: '',
      unit: '',
      rate: null,
      quantity: null,
      total: null,
      revenueCode: null,
      rateSheetItemId: null,
      assignedTech1Initials: null,
      assignedTech2Initials: null,
      assignedTech3Initials: null,
      assignedTech4Initials: null,
      assignedTech1Id: null,
      assignedTech2Id: null,
      assignedTech3Id: null,
      assignedTech4Id: null,
    };
    // A new row has no type yet, so this suggests nothing today — it is here so
    // a row created WITH a type is suggested the same as one typed afterwards.
    const { line, prefilled } = suggestSlots(blank, 'open');
    const index = lineItems.length;
    setLineItems(items => [...items, line]);
    setSlotPrefill(prev => ({ ...prev, [index]: prefilled ? 'prefilled' : 'open' }));
  };

  /** "Add discount" — one helper for both editors, lib/ticket-discount. */
  const addDiscount = (spec: DiscountSpec) => {
    const blank: LineItem = {
      lineItemType: 'other', description: '', unit: null, rate: null, quantity: null, total: null,
      revenueCode: null, rateSheetItemId: null,
      assignedTech1Initials: null, assignedTech2Initials: null, assignedTech3Initials: null, assignedTech4Initials: null,
      assignedTech1Id: null, assignedTech2Id: null, assignedTech3Id: null, assignedTech4Id: null,
    };
    setLineItems(items => [...items, buildDiscountLine(blank, spec, items)]);
  };

  const editDiscount = (index: number, edit: DiscountEdit) => {
    setLineItems(items => items.map((row, i) => (i === index ? editDiscountLine(row, edit, items) : row)));
    // Field-scoped, as for plain rows: a second-Multi-Rig refusal stays until
    // the line is removed, whatever else is typed on it.
    setLineItemErrors(prev => discountFieldsFor(edit).reduce((acc, f) => clearFieldError(acc, index, f), prev));
  };

  // All or nothing: one line whose total is not there makes the subtotal an
  // em dash, not the sum of the others. `Number(li.total) || 0` summed an
  // unpriced line as $0.00 and printed a confident, short subtotal.
  // The lines as shown: a Multi Rig line whose base lines changed on this screen
  // carries the editor's estimate; every other line is exactly what is in state.
  const shownLines = useMemo(() => withDiscountEstimates(lineItems), [lineItems]);
  const subtotal = sumMoney(shownLines.map(li => li.total));

  // Editing, the load finished, and there is no ticket. Never render the form:
  // an empty edit form is indistinguishable from a real one, and saving it
  // overwrites the ticket with blanks.
  if (isEditing && !editLoading && !existingTicket) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <header className="z-40 bg-fieldiq-dark/95 backdrop-blur border-b border-fieldiq-border px-4 py-3 flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-text"><ArrowLeft size={26} /></button>
          <h1 className="text-lg font-bold">Edit Ticket</h1>
        </header>
        <div className="px-4 py-12 text-center text-fieldiq-text">
          <p className="mb-4">This ticket could not be loaded. It may have been deleted.</p>
          <button onClick={() => navigate('/tickets')} className="bg-fieldiq-accent text-fieldiq-dark font-semibold px-6 py-2 rounded-lg">
            Back to Field Tickets
          </button>
        </div>
      </div>
    );
  }

  if (!jobId) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <header className="z-40 bg-fieldiq-dark/95 backdrop-blur border-b border-fieldiq-border px-4 py-3 flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-text"><ArrowLeft size={26} /></button>
          <h1 className="text-lg font-bold">{isEditing ? 'Edit Ticket' : 'New Ticket'}</h1>
        </header>
        <div className="px-4 py-12 text-center text-fieldiq-text">
          <p className="mb-4">Select a job first to create a ticket.</p>
          <button onClick={() => navigate('/jobs')} className="bg-fieldiq-accent text-fieldiq-dark font-semibold px-6 py-2 rounded-lg">
            Go to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-44">
      <div className="border-b border-border px-6 py-4">
        <button onClick={goBack} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">{isEditing ? 'Edit Field Ticket' : 'New Field Ticket'}</h1>
            {isEditing && existingTicket && (
              <>
                <p className="text-xs text-fieldiq-muted">
                  {existingTicket.ticket_number || existingTicket.ticketNumber} · {form.customerName}
                </p>
                {/* Last-write-wins: this is how an office user sees that a hand
                    saved over their edit. Absolute time on hover — desktop. */}
                <LastEditedStamp ticket={existingTicket} className="mt-0.5" />
              </>
            )}
            {!isEditing && job && <p className="text-xs text-fieldiq-muted">{job.job_number ?? job.jobNumber} · {form.customerName}</p>}
          </div>
          <div className="flex items-center gap-3">
            <VoiceRecorder
              onRecordingComplete={(blob) => voiceMutation.mutate(blob)}
              isProcessing={voiceMutation.isPending}
              disabled={jobLoading}
            />
            <button
              onClick={requestAiSuggest}
              disabled={aiMutation.isPending || jobLoading}
              className="flex items-center gap-1.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50"
            >
              {aiMutation.isPending ? (
                <><Loader size={14} className="animate-spin" /> Thinking...</>
              ) : (
                <><Sparkles size={14} /> AI Suggest</>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {message && (
          <div className={`rounded-xl p-3 flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400'
            : message.type === 'warning' ? 'bg-amber-500/20 text-amber-400'
            : 'bg-red-500/20 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {jobLoading ? (
          <div className="flex items-center justify-center py-12 text-fieldiq-text gap-2">
            <Loader size={20} className="animate-spin" /> Loading job data...
          </div>
        ) : (
          <>
            {/* Voice Transcript */}
            {voiceTranscript && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mic size={16} className="text-blue-400" />
                  <h3 className="text-sm font-bold text-blue-400">What I heard</h3>
                </div>
                <p className="text-sm text-fieldiq-text italic">"{voiceTranscript}"</p>
              </div>
            )}

            {/* Rate sheet ambiguity — the draft is unpriced and a human must choose.
                Persistent on purpose: this is a task, not a notification. */}
            {aiRateSheetChoice.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <h3 className="text-sm font-bold text-amber-400">
                    Pricing needs a decision — {aiRateSheetChoice.length} rate sheets match this job
                  </h3>
                </div>
                <p className="text-sm text-fieldiq-text mb-2">
                  These price the same work differently, so nothing can choose between them
                  automatically. Line items were drafted without rates — set the prices from the
                  sheet this job is billed under.
                </p>
                <ul className="text-sm text-fieldiq-text space-y-1">
                  {aiRateSheetChoice.map((s) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <span className="text-amber-400">•</span>
                      <span>{s.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* AI Reasoning */}
            {aiReasoning && (
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-purple-400" />
                  <h3 className="text-sm font-bold text-purple-400">AI Analysis</h3>
                  {aiConfidence && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      aiConfidence === 'high' ? 'bg-green-500/20 text-green-400' :
                      aiConfidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {aiConfidence} confidence
                    </span>
                  )}
                </div>
                <p className="text-sm text-fieldiq-text">{aiReasoning}</p>
              </div>
            )}

            {/* Job Summary */}
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={16} className="text-fieldiq-accent" />
                <h3 className="text-sm font-bold">Job Info</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-fieldiq-muted text-xs">Job</span>
                  <p className="font-medium">{job?.job_number ?? job?.jobNumber ?? existingTicket?.job_number ?? existingTicket?.jobNumber ?? '-'}</p>
                </div>
                <div>
                  <span className="text-fieldiq-muted text-xs">Customer</span>
                  <p className="font-medium">{form.customerName || '-'}</p>
                </div>
                {/* Contact gets its own labelled cell and ALWAYS renders.
                    It used to be an unlabelled line under Customer, shown only
                    when the job carried one — so a contactless job rendered
                    nothing at all, and "the office never entered a contact" was
                    indistinguishable from "this screen does not show contacts".
                    An absent value renders as an em dash, per the rule that a
                    gap must look like a gap.

                    No `jobLoading` branch here: this whole block already sits
                    inside the `jobLoading ? <Loader/> :` at the top of the
                    section, so a load never reaches this dash. "Not yet" is
                    still not "none" — it is just answered one level up. */}
                <div>
                  <span className="text-fieldiq-muted text-xs">Contact</span>
                  <p className="font-medium">
                    {(job?.customer_contact_name ?? job?.customerContactName) || '—'}
                  </p>
                  {(job?.customer_contact_phone ?? job?.customerContactPhone) && (
                    <p className="text-xs text-fieldiq-muted mt-0.5">
                      {job.customer_contact_phone ?? job.customerContactPhone}
                    </p>
                  )}
                </div>
                {form.rigName && (
                  <div>
                    <span className="text-fieldiq-muted text-xs">Rig</span>
                    <p className="font-medium">{form.rigName}</p>
                  </div>
                )}
                {form.lsd && (
                  <div>
                    <span className="text-fieldiq-muted text-xs">LSD</span>
                    <p className="font-medium">{form.lsd}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Form */}
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <FileText size={16} className="text-fieldiq-accent" /> Ticket Details
              </h3>

              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Ticket Number (auto-generated if blank)</label>
                <input
                  type="text"
                  value={form.ticketNumber}
                  onChange={e => setForm(f => ({ ...f, ticketNumber: e.target.value }))}
                  placeholder={nextNumberData?.ticketNumber || 'Leave blank for auto'}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
                {!form.ticketNumber && nextNumberData?.ticketNumber && (
                  <p className="text-xs text-fieldiq-muted mt-1">
                    Will be: <span className="text-fieldiq-accent font-mono">{nextNumberData.ticketNumber}</span>
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Calendar size={12} /> Arrival Date
                  </label>
                  <DatePicker
                    value={form.arrivalDate}
                    onChange={v => setForm(f => ({ ...f, arrivalDate: v }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Clock size={12} /> Arrival Time
                  </label>
                  <TimePicker
                    value={form.arrivalTime}
                    onChange={v => setForm(f => ({ ...f, arrivalTime: v }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Calendar size={12} /> Finish Date
                  </label>
                  <DatePicker
                    value={form.finishDate}
                    onChange={v => setForm(f => ({ ...f, finishDate: v }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Clock size={12} /> Finish Time
                  </label>
                  <TimePicker
                    value={form.finishTime}
                    onChange={v => setForm(f => ({ ...f, finishTime: v }))}
                  />
                </div>
              </div>

              <TicketHoursReadout
                hours={serverHours}
                contradictory={timesContradict}
                dirty={hoursAreStale}
              />

              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Job Type</label>
                <RadioGroup
                  options={JOB_TYPE_OPTIONS}
                  value={form.jobType}
                  onChange={v => setForm(f => ({ ...f, jobType: v }))}
                  columns={3}
                  allowClear
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block">Metres Ran</label>
                  {/* Holding per-string metres, the total is the server's sum of
                      them and is shown, not typed — a typed total would be
                      overwritten on save (api#830). */}
                  {totalIsStringSum(recordedMetres) ? (
                    <p className="px-3 py-2.5 text-sm text-white" data-testid="metres-run-total">
                      {fmtQty(form.metresRan) === '—' ? '—' : `${fmtQty(form.metresRan)} m`}
                    </p>
                  ) : (
                  <input
                    type="number"
                    onWheel={blurOnWheel}
                    value={form.metresRan}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, metresRan: val }));
                      // Auto-update qty for all per-metre line items
                      const metres = val ? parseFloat(val) : null;
                      if (metres != null && !isNaN(metres)) {
                        setLineItems(items => items.map(li => {
                          if (li.unit !== 'per_metre') return li;
                          const total = li.rate != null ? Math.round(Number(li.rate) * metres * 100) / 100 : null;
                          return { ...li, quantity: metres, total };
                        }));
                      }
                    }}
                    placeholder="Total metres"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                  )}
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block">Pipe Size</label>
                  <input
                    type="text"
                    value={form.pipeSize}
                    onChange={e => setForm(f => ({ ...f, pipeSize: e.target.value }))}
                    placeholder={'e.g. 5.5", 7"'}
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block">Connections</label>
                  <ConnectionTypeSelect
                    value={form.connections}
                    onChange={v => setForm(f => ({ ...f, connections: v }))}
                  />
                </div>
              </div>

              {metresStrings.length > 0 && (
                <CasingStringMetresFields
                  strings={metresStrings}
                  draft={metresDraft}
                  onChange={(id, v) => setMetresDraft(d => ({ ...d, [id]: v }))}
                  recorded={recordedMetres}
                  totalFromApi={existingTicket?.actual_metres_run ?? existingTicket?.actualMetresRun}
                  labelClassName="text-xs text-fieldiq-muted mb-1 block"
                  inputClassName="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
              )}

              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Internal Notes</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Internal only — not visible to customers"
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Customer Notes</label>
                <textarea
                  rows={2}
                  value={form.customerNotes}
                  onChange={e => setForm(f => ({ ...f, customerNotes: e.target.value }))}
                  placeholder="Visible to customer on approval page"
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none resize-none"
                />
              </div>
            </div>

            {/* Equipment Used */}
            {equipmentUsage.length > 0 && (
              <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
                <h3 className="text-sm font-bold flex items-center gap-2 mb-3">
                  <Wrench size={16} className="text-fieldiq-accent" />
                  Equipment Used ({equipmentUsage.filter(e => e.wasUsed).length}/{equipmentUsage.length})
                </h3>
                <div className="space-y-2">
                  {equipmentUsage.map((item, i) => (
                    <div
                      key={item.equipmentId}
                      className={`flex flex-wrap items-center gap-3 bg-fieldiq-dark rounded-xl px-4 py-3 border border-fieldiq-border ${!item.wasUsed ? 'opacity-50' : ''}`}
                    >
                      {/* Toggle */}
                      <button
                        type="button"
                        onClick={() =>
                          setEquipmentUsage(prev =>
                            prev.map((eq, j) => j === i ? { ...eq, wasUsed: !eq.wasUsed } : eq)
                          )
                        }
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${item.wasUsed ? 'bg-green-500' : 'bg-fieldiq-border'}`}
                      >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${item.wasUsed ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                      {/* Name + type */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{item.name}</p>
                        {item.type && <p className="text-xs text-fieldiq-muted">{item.type}</p>}
                      </div>
                      {/* Days stepper */}
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={!item.wasUsed || (item.daysMode === 'crew' && item.daysUsed <= 1)}
                          onClick={() =>
                            setEquipmentUsage(prev =>
                              prev.map((eq, j) => j === i ? { ...eq, ...stepDays(eq, -1) } : eq)
                            )
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-fieldiq-border text-fieldiq-text text-sm font-bold hover:bg-fieldiq-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          −
                        </button>
                        <span className={`w-8 text-center text-sm font-semibold ${item.wasUsed ? 'text-white' : 'text-fieldiq-muted'}`}>
                          {/* "auto" until the crew chooses: the API then takes the ticket's
                              days worked (a new row) or keeps what is stored. */}
                          {item.daysMode === 'auto' ? 'auto' : item.daysUsed}
                        </span>
                        <button
                          type="button"
                          disabled={!item.wasUsed}
                          onClick={() =>
                            setEquipmentUsage(prev =>
                              prev.map((eq, j) => j === i ? { ...eq, ...stepDays(eq, 1) } : eq)
                            )
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-fieldiq-border text-fieldiq-text text-sm font-bold hover:bg-fieldiq-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                        <span className={`text-xs ml-1 ${item.wasUsed ? 'text-fieldiq-muted' : 'text-fieldiq-muted/50'}`}>
                          {item.daysMode === 'auto' ? 'days' : item.daysUsed === 1 ? 'day' : 'days'}
                        </span>
                        {item.wasUsed && item.daysMode === 'crew' && (
                          <button
                            type="button"
                            aria-label={`${item.name || 'Equipment'} days back to auto`}
                            onClick={() =>
                              setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, daysMode: 'auto' } : eq))
                            }
                            className="text-[11px] text-fieldiq-accent hover:underline ml-1"
                          >
                            auto
                          </button>
                        )}
                      </div>
                      {item.wasUsed && (
                        <EquipmentHoursRuns
                          item={item}
                          ticketHours={serverHours}
                          onChange={(patch) =>
                            setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, ...patch } : eq))
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-y-1 mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <DollarSign size={16} className="text-fieldiq-accent" />
                  Line Items ({lineItems.length})
                </h3>
                <div className="flex items-center gap-3">
                  <AddDiscountButton lines={shownLines} onAdd={addDiscount} />
                  <button
                    onClick={addLineItem}
                    className="text-xs flex items-center gap-1 text-fieldiq-accent"
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>
              </div>

              {lineItems.length === 0 ? (
                <div className="text-center py-6 text-fieldiq-muted text-sm">
                  <Info size={20} className="mx-auto mb-2" />
                  <p>No line items yet.</p>
                  <p className="text-xs mt-1">Tap <strong>AI Suggest</strong> to auto-generate from rate sheets, or <strong>Add Item</strong> and search the price book.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {shownLines.map((item, i) => isDiscountLine(item) ? (
                    <DiscountLineRow
                      key={i}
                      line={item}
                      index={i}
                      lines={shownLines}
                      reasons={lineItemErrors[i]}
                      onEdit={edit => editDiscount(i, edit)}
                      onRemove={() => removeLineItem(i)}
                      rowRef={el => { lineItemRowRefs.current[i] = el; }}
                    />
                  ) : (
                    <div
                      key={i}
                      data-testid="line-item-row"
                      ref={el => { lineItemRowRefs.current[i] = el; }}
                      className={`bg-fieldiq-dark rounded-xl p-3 space-y-2 ${rowErrorRingClass(!!lineItemErrors[i])}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-1">
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Description</label>
                            <RateSheetItemPicker
                              value={item.description ?? ''}
                              onChange={v => updateLineItem(i, 'description', v)}
                              onSelect={picked => pickRateSheetItem(i, picked)}
                              items={rateSheetItems ?? []}
                              state={rateItemsState}
                              preferredSheetId={jobRateSheetId}
                              surface="card"
                              // Scoped to the reasons that render under THIS box. A refusal about
                              // the rate used to redden the description too, which sends the
                              // reader to a box that has nothing wrong with it.
                              invalid={splitRowReasons(lineItemErrors[i]).other.length > 0}
                              ariaLabel="Description"
                              placeholder={item.lineItemType === 'other' ? 'Describe this charge\u2026' : 'Search the price book, or type your own'}
                              linkState={priceBookLinkState(item)}
                              onOffBookChange={v => setLineOffBook(i, v)}
                              onAutoLink={match => autoLinkLine(i, match)}
                              rowUnit={item.unit}
                            />
                            {/*
                              The reasons that are NOT about the two money
                              boxes. Those render beside the boxes themselves
                              further down, so the words sit under the input
                              they name. See splitRowReasons for why this half
                              is the complement rather than an allow-list.
                            */}
                            {splitRowReasons(lineItemErrors[i]).other.length > 0 && (
                              <RowError reasons={splitRowReasons(lineItemErrors[i]).other} />
                            )}
                            {lineItemUnitNotes[i] && (
                              <p className="text-[11px] text-amber-400 mt-0.5">{lineItemUnitNotes[i]}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Type</label>
                            <select
                              aria-label="Type"
                              value={item.lineItemType}
                              onChange={e => updateLineItem(i, 'lineItemType', e.target.value)}
                              className={`w-full bg-fieldiq-card rounded px-2 py-1 text-xs border outline-none ${lineItemTypeErrors[i] ? 'border-red-500/60 text-red-400' : 'border-fieldiq-border'}`}
                            >
                              <option value="">Select type…</option>
                              {visibleLineItemTypeOptions(hiddenLineItemTypes, item.lineItemType).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            {lineItemTypeErrors[i] && <RowError reasons={lineItemTypeErrors[i]} />}
                          </div>
                          {item.revenueCode && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-fieldiq-muted">Code: {item.revenueCode}</span>
                            </div>
                          )}
                        </div>
                        <button aria-label={`Remove line ${i + 1}`} onClick={() => removeLineItem(i)} className="p-1 text-red-400 hover:text-red-300">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-[10px] text-fieldiq-muted">Unit</label>
                          <select
                            aria-label="Unit"
                            value={item.unit ?? ''}
                            onChange={e => updateLineItem(i, 'unit', e.target.value || null)}
                            className="w-full bg-fieldiq-card rounded px-2 py-1 text-xs border border-fieldiq-border outline-none"
                          >
                            <option value="">Select...</option>
                            {UNIT_OPTIONS.map(u => (
                              <option key={u.value} value={u.value}>{u.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-fieldiq-muted">Rate</label>
                          <input
                            type="number"
                            aria-label="Rate"
                            step="0.01"
                            onWheel={blurOnWheel}
                            value={item.rate ?? ''}
                            onChange={e => updateLineItem(i, 'rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                            className={`w-full bg-fieldiq-card rounded px-2 py-1 text-xs border outline-none ${fieldErrorClass(lineItemErrors[i], 'rate')}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-fieldiq-muted">Qty</label>
                          <input
                            type="number"
                            aria-label="Qty"
                            step="0.01"
                            onWheel={blurOnWheel}
                            value={item.quantity ?? ''}
                            onChange={e => updateLineItem(i, 'quantity', e.target.value === '' ? null : parseFloat(e.target.value))}
                            className={`w-full bg-fieldiq-card rounded px-2 py-1 text-xs border outline-none ${fieldErrorClass(lineItemErrors[i], 'quantity')}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-fieldiq-muted">Total</label>
                          <div className="bg-fieldiq-card rounded px-2 py-1 text-xs border border-fieldiq-border text-green-400 font-medium">
                            {fmtCurrency(item.total)}
                          </div>
                        </div>
                      </div>
                      {/*
                        Under the four boxes rather than under one of them: the
                        sentence is about the pair, and a column this narrow
                        would wrap it to five lines.
                      */}
                      {splitRowReasons(lineItemErrors[i]).money.length > 0 && (
                        <RowError reasons={splitRowReasons(lineItemErrors[i]).money} />
                      )}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {([1, 2, 3, 4] as const).map(slot => {
                          const heldId = item[`assignedTech${slot}Id` as const];
                          const slotLists = pickerListsForSlot(crewLists, showEveryone, heldId);
                          const orphan = orphanSlotOption(slotLists, heldId, item[`assignedTech${slot}Initials` as const]);
                          return (
                            <div key={slot}>
                              <label className="text-[10px] text-fieldiq-muted">Tech {slot}</label>
                              <select
                                aria-label={`Tech ${slot}`}
                                value={heldId ?? ''}
                                onChange={e => setLineItemTech(i, slot, e.target.value || null)}
                                className="w-full bg-fieldiq-card rounded px-2 py-1 text-xs border border-fieldiq-border outline-none"
                              >
                                <option value="">None</option>
                                {slotLists.crew.map((c: any) => (
                                  <option key={crewId(c)} value={crewId(c)}>{crewLabel(c)}</option>
                                ))}
                                {slotLists.others.length > 0 && (
                                  <optgroup label="Not on this job">
                                    {slotLists.others.map((c: any) => (
                                      <option key={crewId(c)} value={crewId(c)}>{crewLabel(c)}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {orphan ? <option value={crewId(orphan)}>{crewLabel(orphan)}</option> : null}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                      {slotPrefill[i] === 'prefilled' && (
                        <p className="text-[11px] text-fieldiq-muted">Pre-filled from job positions — saving confirms it.</p>
                      )}
                      {isOnline && (
                        <div>
                          <button
                            type="button"
                            onClick={() => setShowEveryone(v => !v)}
                            className="text-[10px] text-fieldiq-accent"
                          >
                            {showEveryone ? 'Job crew only' : 'Not on this job? Show everyone'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Subtotal */}
                  <div className="flex items-center justify-between pt-2 border-t border-fieldiq-border">
                    <span className="text-sm font-bold">Subtotal</span>
                    <span data-testid="editor-subtotal" className="text-lg font-bold text-green-400">
                      {fmtCurrency(subtotal, currency)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom Action Bar */}
      {!jobLoading && !editLoading && (
        <div className="border-t border-fieldiq-border p-3 mt-4">
          <div className="max-w-[1920px] mx-auto">
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(true)}
                className="bg-fieldiq-card border border-fieldiq-border text-white py-2.5 px-3 rounded-xl text-sm hover:border-fieldiq-accent transition-colors flex items-center justify-center gap-1.5"
              >
                <Eye size={14} /> Preview
              </button>
              {isEditing ? (
                <>
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={createMutation.isPending}
                    className="flex-1 bg-fieldiq-card border border-fieldiq-border text-white py-2.5 rounded-xl font-medium text-sm hover:border-fieldiq-accent transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Save size={14} /> {createMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                  {/* Only where the API will actually perform the transition.
                      On a field_approved or office_approved ticket this button
                      did nothing a plain Save doesn't — it named a status
                      change, sent a request the API refused, and reported the
                      refusal over a save that had worked. Save alone is the
                      honest control there, and it fills the row. */}
                  {canSubmit && (
                    <button
                      onClick={() => handleSubmit(false)}
                      disabled={createMutation.isPending}
                      className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Send size={14} /> Submit &amp; Field Approve
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={createMutation.isPending}
                    className="flex-1 bg-fieldiq-card border border-fieldiq-border text-white py-2.5 rounded-xl font-medium text-sm hover:border-fieldiq-accent transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Save size={14} /> Save Draft
                  </button>
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={createMutation.isPending}
                    className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Send size={14} /> {createMutation.isPending ? 'Saving...' : 'Submit & Field Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (() => {
        const gstRate = currency === 'CAD' ? 0.05 : 0;
        // An unknown subtotal makes both of these unknown too, never $0.00.
        const gstAmount = subtotal === null ? null : subtotal * gstRate;
        const grandTotal = subtotal === null || gstAmount === null ? null : subtotal + gstAmount;
        // Hours as stored by the server. Blank on an unsaved ticket (nothing
        // has been computed yet) — FieldTicketDisplay omits the row. Printing
        // a locally-derived number here would put a figure on a customer-
        // facing ticket that doesn't match the one on the invoice.
        const hoursOnLocation = formatTicketHours(
          existingTicket?.total_hours ?? existingTicket?.totalHours
        ) ?? '';
        // The customer's SIGNED approval stamp, when there is one. A ticket not
        // yet approved has no frozen copy on it, and the live template is not
        // this screen's to show — the customer fills it on the review page.
        const signedStamp = frozenStampFromTicket(existingTicket);
        /** work_date is a calendar day. @see formatCalendarDate. */
        const formatDateNice = (d: string | null | undefined) =>
          formatCalendarDate(d, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '';
        const formatTimeNice = (t: string) => {
          if (!t) return '';
          const [h, m] = t.split(':');
          return `${h}:${m}`;
        };

        return (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto">
            <div className="w-full max-w-3xl mx-4 my-6 bg-white text-black shadow-2xl">
              {/* Action Bar (not printed) */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-100 border-b border-gray-300 print:hidden">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Preview</span>
                {previewDocError && (
                  <span role="alert" className="mx-2 min-w-0 flex-1 truncate text-xs text-red-700" title={previewDocError}>
                    {previewDocError}
                  </span>
                )}
                <div className="flex items-center gap-1">
                  {/* Print and Save produce THE SERVER'S DOCUMENT, GET
                      /tickets/:id/pdf — never `window.print()` over this
                      preview. This preview draws a signed stamp's signature as
                      a word and none of what the server PDF prints, so printing
                      it handed the office a ticket with no signature on it
                      (Smith FT-00062). An unsaved ticket has no server
                      document yet, so the two are disabled until it is saved
                      and say so. See lib/ticket-pdf.ts. */}
                  <Tooltip content={editTicketId ? 'Print' : 'Save the ticket first to print it'}>
                    <button
                      onClick={() => previewPdf('print')}
                      disabled={!editTicketId}
                      className="p-2 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                      aria-label="Print"
                    >
                      <Printer size={16} />
                    </button>
                  </Tooltip>
                  <Tooltip content={editTicketId ? 'Save as PDF' : 'Save the ticket first to download its PDF'}>
                    <button
                      onClick={() => previewPdf('save')}
                      disabled={!editTicketId}
                      className="p-2 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                      aria-label="Save as PDF"
                    >
                      <Download size={16} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Email">
                    <button
                      onClick={() => {
                        const subject = `Field Ticket - ${form.ticketNumber || 'Draft'} - ${form.customerName}`;
                        const body = `Please find attached field ticket ${form.ticketNumber || 'Draft'} for ${form.customerName}.`;
                        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                      }}
                      className="p-2 rounded hover:bg-gray-200 text-gray-600" aria-label="Email"
                    >
                      <Mail size={16} />
                    </button>
                  </Tooltip>
                  <button onClick={() => setShowPreview(false)} className="p-2 rounded hover:bg-gray-200 text-gray-600">
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* ─── Ticket Content (shared FieldTicketDisplay) ─── */}
              <div id="ticket-preview" className="p-4 print:p-4">
                <FieldTicketDisplay
                  ticketNumber={form.ticketNumber || 'AUTO'}
                  companyName={company?.displayName || company?.name || 'Company Name'}
                  customerName={form.customerName}
                  jobType={form.jobType}
                  rigName={form.rigName}
                  casingSize={form.pipeSize}
                  connectionType={form.connections}
                  metresRun={form.metresRan}
                  workDate={formatDateNice(form.arrivalDate)}
                  startTime={formatTimeNice(form.arrivalTime)}
                  endTime={formatTimeNice(form.finishTime)}
                  totalHours={hoursOnLocation}
                  lineItems={withoutDiscountEstimates(shownLines).map(li => ({
                    type: li.lineItemType,
                    description: li.revenueCode
                      ? `${li.revenueCode} - ${li.description || getLineItemTypeLabel(li.lineItemType)}`
                      : (li.description || getLineItemTypeLabel(li.lineItemType)),
                    quantity: li.quantity ?? 0,
                    unit: li.unit || '',
                    rate: li.rate ?? 0,
                    // A discount's amount is never coerced: an estimate was taken
                    // out above and must read as absent, not as $0.00.
                    total: isDiscountLine(li) ? li.total : (li.total ?? 0),
                  }))}
                  subtotal={subtotal}
                  gstAmount={currency === 'CAD' ? gstAmount : undefined}
                  total={grandTotal}
                  currency={currency}
                  customerNotes={form.customerNotes}
                  crew={crew.map((c: any) => ({
                    name: c.name || c.initials || '',
                    role: c.role || '',
                  }))}
                  stamp={signedStamp ? frozenStampDisplay(signedStamp) : undefined}
                  stampSignature={previewStampSignature}
                />
              </div>

              {/* ─── Modal Footer Actions (not printed) ─── */}
              <div className="px-4 py-3 bg-gray-100 border-t border-gray-300 flex gap-2 print:hidden">
                <button
                  onClick={() => setShowPreview(false)}
                  className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => { setShowPreview(false); handleSubmit(true); }}
                  disabled={createMutation.isPending}
                  className="flex-1 bg-secondary text-white py-2.5 rounded-lg font-medium text-sm hover:bg-card transition flex items-center justify-center gap-1.5"
                >
                  {/* "Save Draft" is only true of a ticket that is one. On an
                      edit this saves in place, or reverts an office-approved
                      ticket to Field Approved — neither of which produces a
                      draft, and with the amber button gone below this is the
                      only control in the footer. */}
                  <Save size={14} /> {isEditing ? 'Save' : 'Save Draft'}
                </button>
                {/* Same gate as the bottom action bar — the modal is a second
                    door to the same handleSubmit, and a button that names a
                    transition the API refuses is the bug either way. */}
                {canSubmit && (
                  <button
                    onClick={() => { setShowPreview(false); handleSubmit(false); }}
                    disabled={createMutation.isPending}
                    className="flex-1 bg-amber-500 text-black py-2.5 rounded-lg font-semibold text-sm hover:bg-amber-400 transition flex items-center justify-center gap-1.5"
                  >
                    <Send size={14} /> Submit &amp; Field Approve
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Saving an edit to an office-approved ticket reverts it. Every
          consequence listed here is one the API actually performs (#311), in
          the vocabulary this app renders — "Field Approved", not the enum
          `field_approved`, and not "pending office approval", which no screen
          says. */}
      <ConfirmModal
        isOpen={aiReplaceConfirm && !!aiReplaceCopy}
        title={aiReplaceCopy?.title ?? ''}
        body={aiReplaceCopy?.body ?? ''}
        confirmLabel={aiReplaceCopy?.confirmLabel ?? ''}
        confirmVariant="destructive"
        onCancel={() => setAiReplaceConfirm(false)}
        onConfirm={() => {
          setAiReplaceConfirm(false);
          aiMutation.mutate(jobId!);
        }}
      />
      <ConfirmModal
        isOpen={!!pendingRevertSave}
        title="This will reopen the ticket"
        confirmLabel="Save and reopen"
        confirmVariant="destructive"
        loading={createMutation.isPending}
        onCancel={() => setPendingRevertSave(null)}
        onConfirm={() => {
          const payload = pendingRevertSave;
          setPendingRevertSave(null);
          if (payload) createMutation.mutate(payload);
        }}
        body={
          <>
            <p>
              Ticket {existingTicket?.ticket_number ?? existingTicket?.ticketNumber ?? ''} is{' '}
              <span className="text-white font-medium">{officeTicketStatusLabel('office_approved')}</span>.
              Saving these changes will:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Return it to{' '}
                <span className="text-white font-medium">{officeTicketStatusLabel('field_approved')}</span>,
                so it has to be office-approved again.
              </li>
              <li>
                Stop the customer-facing PDF from being produced until it is re-approved. A watermarked
                preview stays available.
              </li>
              <li>
                Break the approval link already sent to the customer. They will not be able to sign until
                you re-approve and send it again.
              </li>
            </ul>
          </>
        }
      />
    </div>
  );
}
